import { useState } from 'react';
import { Bell } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useCreatePriceAlert } from '@/hooks/usePriceAlerts';
import type { AlertType } from '@/data/priceAlerts';

interface PriceAlertModalProps {
  productId: string;
  productTitle: string;
  currentPrice: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PriceAlertModal({
  productId,
  productTitle,
  currentPrice,
  open,
  onOpenChange,
}: PriceAlertModalProps) {
  const [targetPrice, setTargetPrice] = useState(parseFloat((currentPrice * 0.8).toFixed(2))); // Default to 20% discount
  const [alertType, setAlertType] = useState<AlertType>('below');
  const createAlert = useCreatePriceAlert();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createAlert.mutate(
      {
        productId,
        targetPrice: parseFloat(targetPrice.toFixed(2)),
        alertType,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      }
    );
  };

  const suggestedPrice = parseFloat((currentPrice * 0.8).toFixed(2));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary" />
            Set Price Alert
          </DialogTitle>
          <DialogDescription>
            Get notified when <strong>{productTitle}</strong> hits your target price.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Alert Type */}
          <div className="space-y-2">
            <Label>Alert Type</Label>
            <RadioGroup value={alertType} onValueChange={(v) => setAlertType(v as AlertType)}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="below" id="below" />
                <Label htmlFor="below" className="font-normal cursor-pointer">
                  Notify when price drops below target
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="any_change" id="any_change" />
                <Label htmlFor="any_change" className="font-normal cursor-pointer">
                  Notify on any price change
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="sale" id="sale" />
                <Label htmlFor="sale" className="font-normal cursor-pointer">
                  Notify when on sale
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Target Price */}
          {alertType === 'below' && (
            <div className="space-y-2">
              <Label htmlFor="target">Target Price</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  $
                </span>
                <Input
                  id="target"
                  type="number"
                  step="0.01"
                  min="0"
                  value={targetPrice.toFixed(2)}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    setTargetPrice(isNaN(value) ? 0 : parseFloat(value.toFixed(2)));
                  }}
                  className="pl-7"
                  required
                />
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Current: ${currentPrice.toFixed(2)}</span>
                <button
                  type="button"
                  onClick={() => setTargetPrice(suggestedPrice)}
                  className="text-green-700 hover:text-green-800 hover:underline font-medium"
                >
                  Suggest: ${suggestedPrice.toFixed(2)} (20% off)
                </button>
              </div>
            </div>
          )}

          {/* Price Context */}
          <div className="rounded-lg bg-muted p-3 text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">We'll email you when:</p>
            {alertType === 'below' && (
              <p>The price drops to ${targetPrice.toFixed(2)} or lower</p>
            )}
            {alertType === 'any_change' && (
              <p>The price changes from the current ${currentPrice.toFixed(2)}</p>
            )}
            {alertType === 'sale' && (
              <p>This product goes on sale or gets a special promotion</p>
            )}
          </div>

          {/* Submit */}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createAlert.isPending} className="flex-1">
              {createAlert.isPending ? 'Creating...' : 'Create Alert'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
